/**
 * compliance_evaluate — safe agent tool that runs the compliance
 * evaluator for a folder + framework via the existing REST endpoint.
 *
 * Tier: safe (read-only, no mutations).
 * See `docs/core/agent-tool-architecture.md`.
 */

import type { IAgentTool, IAgentToolContext, AgentToolResult } from '../../../../core/ai/agent/IAgentTool';
import { CompassTracking } from '../../services/CompassTracking';

interface ComplianceReport {
  graphId: string;
  framework: string;
  frameworkLabel: string;
  evaluatedAt: string;
  graphVersion: number;
  summary: { total: number; compliant: number; warning: number; violation: number; unknown: number };
  controls: Array<{
    controlId: string;
    controlLabel: string;
    status: 'compliant' | 'warning' | 'violation' | 'unknown';
    reason: string;
    evidenceNodeIds: string[];
    processNodeIds: string[];
    findings: Array<{ severity: string; message: string }>;
    artifactCount?: number;
  }>;
  pillarRollup?: Array<{
    pillar: string;
    hri: number;
    mri: number;
    none: number;
    unknown: number;
  }>;
}

function getFolderId(context: IAgentToolContext): string | null {
  const ctx = context.assistantContext as Record<string, unknown> | undefined;
  const f = ctx?.folderId;
  return typeof f === 'string' && f.length > 0 ? f : null;
}

export const complianceEvaluateTool: IAgentTool = {
  name: 'compliance_evaluate',
  description:
    'Run the compliance evaluator for the current folder against a framework (default soc2). Returns per-control status (compliant / warning / violation / unknown) plus a 4-way summary. Use when the user asks to evaluate, audit, run compliance, or check controls.',
  parameters: {
    type: 'object',
    properties: {
      framework: {
        type: 'string',
        description: 'Framework id, e.g. "soc2". Defaults to soc2.',
      },
    },
  },
  supportedDiagramTypes: [],
  tier: 'safe',
  category: 'query',

  async execute(params, context): Promise<AgentToolResult> {
    const folderId = getFolderId(context);
    if (!folderId) {
      return {
        success: false,
        summary:
          'No folder context available. Open this assistant from inside a folder to evaluate compliance.',
      };
    }
    const framework = typeof params.framework === 'string' && params.framework.length > 0 ? params.framework : 'soc2';

    const url = `/api/compass/folders/${encodeURIComponent(folderId)}/compliance/${encodeURIComponent(framework)}/report`;
    let response: Response;
    try {
      response = await fetch(url);
    } catch (err) {
      return { success: false, summary: `Network error fetching ${framework} report: ${(err as Error).message}` };
    }
    if (!response.ok) {
      return { success: false, summary: `${framework} report request failed (${response.status})` };
    }
    const report = (await response.json()) as ComplianceReport;
    const s = report.summary;
    const pct = s.total > 0 ? Math.round((s.compliant / s.total) * 100) : 0;
    const topGaps = report.controls
      .filter((c) => c.status === 'violation' || c.status === 'warning')
      .slice(0, 5)
      .map((c) => `${c.controlId}: ${c.status} — ${c.reason}`)
      .join('\n');

    CompassTracking.complianceReportRun({
      folderId,
      framework,
      clausesChecked: s.total,
    });
    const gapCount = s.violation + s.warning;
    if (gapCount > 0) {
      CompassTracking.gapsFound({
        folderId,
        framework,
        gapCount,
        severityBreakdown: { violation: s.violation, warning: s.warning, unknown: s.unknown },
      });
    }
    if (framework === 'well-architected') {
      CompassTracking.wellArchitectedReviewCompleted({
        folderId,
        graphId: report.graphId,
        evaluatedAt: report.evaluatedAt,
        hriCount: report.pillarRollup?.reduce((sum, pillar) => sum + pillar.hri, 0) ?? 0,
        mriCount: report.pillarRollup?.reduce((sum, pillar) => sum + pillar.mri, 0) ?? 0,
        unknownCount: report.pillarRollup?.reduce((sum, pillar) => sum + pillar.unknown, 0) ?? s.unknown,
      });
    }

    return {
      success: true,
      summary:
        `${report.frameworkLabel}: ${pct}% compliant (${s.compliant}/${s.total}). ` +
        `${s.violation} violation(s), ${s.warning} warning(s), ${s.unknown} unknown.` +
        (topGaps ? `\n\nTop gaps:\n${topGaps}` : ''),
      data: { report },
      uiSurface: 'chat',
    };
  },
};
